import { useEffect, useRef, useState } from 'react';
import { hasAuthToken, getAuthToken, getAccountId } from '../services/apiClient';

/**
 * Custom hook for Server-Sent Events (SSE) connection to receive real-time transfer updates
 * Falls back to polling if SSE connection fails
 */
export function useTransferEvents(onTransferUpdate) {
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000; // 3 seconds

  useEffect(() => {
    // Only connect if user is authenticated
    if (!hasAuthToken()) {
      return;
    }

    let mounted = true;

    const connect = () => {
      
      // Clean up existing connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      try {
        // Get auth token and account ID from apiClient
        const token = getAuthToken();
        const accountId = getAccountId();
        
        if (!token) {
          console.warn('[SSE] No auth token available, skipping connection');
          if (mounted) {
            setIsConnected(false);
          }
          return;
        }
        
        // Create EventSource connection
        // Note: EventSource doesn't support custom headers in standard implementation
        // We use query params for authentication and account context
        const params = new URLSearchParams({
          token: token,
        });
        if (accountId) {
          params.append('accountId', accountId);
        }
        const url = `/api/transfers/events?${params.toString()}`;
        console.log('[SSE] Connecting to SSE stream');
        const eventSource = new EventSource(url);

        eventSource.onopen = () => {
          if (mounted) {
            console.log('[SSE] Connected to transfer events stream');
            setIsConnected(true);
            
            // IMPORTANT: When reconnecting after disconnection, trigger a refresh
            // This catches any events that were missed while disconnected
            if (reconnectAttempts > 0 && onTransferUpdate) {
              console.log('[SSE] Reconnected after disconnect - triggering refresh');
              // Signal parent to refresh data (pass a special "refresh" event)
              onTransferUpdate({ type: 'reconnected', refresh: true });
            }
            
            setReconnectAttempts(0);
          }
        };

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.event === 'connected') {
              console.log('[SSE] Connection confirmed:', data);
            } else if (data.event === 'heartbeat') {
              // Heartbeat received, connection is alive
              // No action needed
            }
          } catch (error) {
            console.warn('[SSE] Failed to parse message:', error);
          }
        };

        // Listen for transfer update events
        eventSource.addEventListener('transfer:update', (event) => {
          try {
            const data = JSON.parse(event.data);
            if (mounted && onTransferUpdate) {
              console.log('[SSE] Transfer update received:', data);
              onTransferUpdate(data);
            }
          } catch (error) {
            console.warn('[SSE] Failed to parse transfer update:', error);
          }
        });

        // Listen for heartbeat events
        eventSource.addEventListener('heartbeat', (event) => {
          // Connection is alive, no action needed
        });

        eventSource.onerror = (error) => {
          console.warn('[SSE] Connection error:', error);
          if (mounted) {
            setIsConnected(false);
            eventSource.close();

            // Attempt to reconnect with exponential backoff
            if (reconnectAttempts < maxReconnectAttempts) {
              const delay = reconnectDelay * Math.pow(2, reconnectAttempts);
              console.log(`[SSE] Reconnecting in ${delay}ms (attempt ${reconnectAttempts + 1}/${maxReconnectAttempts})`);
              reconnectTimeoutRef.current = setTimeout(() => {
                if (mounted) {
                  setReconnectAttempts(prev => prev + 1);
                  connect();
                }
              }, delay);
            } else {
              console.error('[SSE] Max reconnection attempts reached, falling back to polling');
            }
          }
        };

        eventSourceRef.current = eventSource;
      } catch (error) {
        console.error('[SSE] Failed to create EventSource:', error);
        if (mounted) {
          setIsConnected(false);
        }
      }
    };

    // Initial connection
    connect();

    // Cleanup on unmount
    return () => {
      mounted = false;
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [onTransferUpdate, reconnectAttempts]);

  return {
    isConnected,
    reconnectAttempts,
  };
}
